import { sortBy } from 'lodash'

import store from '../../../../../../store'
import { hasPartialServiceAccessTier } from 'hooks/useHasPermission'
import { isObjectEmpty } from 'utils/helpers'
import {
  derivePlatformSelectionFromSetup,
  platformIncludesGoogle,
  platformIncludesMeta,
  SetupPlatform,
} from 'components/Recommendations/Campaign/platform'
import {
  GoogleCampaignType,
  deriveCampaignTypeFromSetup,
} from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'

export const TARGETING_MODES = {
  fdTargeting: 'fd_targeting',
  postalCodes: 'postal_codes',
  rangeLocations: 'range_locations',
}

export const TARGETING_PLATFORMS = { fb: 'fb', google: 'google' }

export function activeTargetingPlatforms(platform) {
  const platforms = []
  if (platformIncludesMeta(platform)) platforms.push(TARGETING_PLATFORMS.fb)
  if (platformIncludesGoogle(platform)) platforms.push(TARGETING_PLATFORMS.google)
  return platforms
}

export function isGoogleDisplaySetup(setup) {
  return (
    platformIncludesGoogle(derivePlatformSelectionFromSetup(setup)) &&
    deriveCampaignTypeFromSetup(setup) === GoogleCampaignType.display
  )
}

/**
 * Platforms whose geo targeting comes from one campaign-wide control (TC #1)
 * instead of each TC's own entry. Only Google Display forces this (the Ads API
 * allows campaign-level geo only), and Meta joins when combined with it.
 *
 * @returns {string[]} gated platform keys, empty for every other combination
 */
export function gatedTargetingPlatforms(setup) {
  const platforms = []
  if (isGoogleDisplaySetup(setup)) {
    platforms.push(TARGETING_PLATFORMS.google)
    if (derivePlatformSelectionFromSetup(setup) === SetupPlatform.both)
      platforms.push(TARGETING_PLATFORMS.fb)
  }
  return platforms
}

/**
 * Example: {google[display], fb} -> {fb}
 * Or {google[display], fb} -> {google[display]}
 * @param {object} setup the current setup object
 * @param {object} nextSetup the next setup object
 * @returns {string[]} platforms that were gated under setup but are no longer gated under nextSetup
 */
export function droppedGatedPlatforms(setup, nextSetup) {
  const next = gatedTargetingPlatforms(nextSetup)
  return gatedTargetingPlatforms(setup).filter((platform) => !next.includes(platform))
}

// The only fields that move to/from campaign_targeting under a gated platform;
// audience and age_range always stay on each TC's own entry.
export const GEO_TARGETING_FIELDS = ['targeting_mode', 'postal_codes', 'range_locations']

export function pickGeoTargetingFields(platformTargeting) {
  if (!platformTargeting) return {}
  const picked = {}
  for (const field of GEO_TARGETING_FIELDS) {
    if (field in platformTargeting) picked[field] = platformTargeting[field]
  }
  return picked
}

export function pickNonGeoTargetingFields(platformTargeting) {
  if (!platformTargeting) return {}
  return Object.fromEntries(
    Object.entries(platformTargeting).filter(([field]) => !GEO_TARGETING_FIELDS.includes(field)),
  )
}

function hasOwnGeoEntry(platformTargeting) {
  return !!platformTargeting && GEO_TARGETING_FIELDS.some((field) => field in platformTargeting)
}

/**
 * Resolves a TC's targeting against setup.campaign_targeting for gated
 * platforms, overlaying only GEO_TARGETING_FIELDS so audience/age survive.
 *
 * Every TC's own entry is geo-stripped on save, so a non-first TC always reads
 * its geo from campaign_targeting. TC #1 owns the campaign-wide edit, so its
 * own entry wins while a save is still in flight — fall back only when it has
 * none of GEO_TARGETING_FIELDS present at all (a content-based emptiness check
 * would stomp a freshly picked, uncommitted mode on every render).
 *
 * @param {boolean} isFirstTc whether this is the campaign-wide-editing TC;
 *   callers pass the UI's own stable index, never setup.creatives[0], whose
 *   order is not an identity signal
 */
export function withCampaignWideTargetingFallback(tcTargeting, setup, isFirstTc) {
  const gated = gatedTargetingPlatforms(setup)
  if (!tcTargeting || !gated.length) return tcTargeting

  const result = { ...tcTargeting }
  for (const platform of gated) {
    const campaignGeo = setup.campaign_targeting?.[platform]
    if (!campaignGeo) continue
    if (isFirstTc && hasOwnGeoEntry(tcTargeting[platform])) continue
    result[platform] = { ...tcTargeting[platform], ...pickGeoTargetingFields(campaignGeo) }
  }
  return result
}

export const actions = {
  // Replaces both platforms' targeting sub-objects at once: the Location modal
  // edits one shared location list with per-entry platform tags.
  setTcTargeting: 'setTcTargeting',
  overwriteTargetingValue: 'overwriteTargeting',
  setSuccess: 'setSuccess',
  setError: 'setError',
  setLoading: 'setLoading',
  reset: 'reset',
}

const _initialState = {
  value: [],
  loading: false,
  error: null,
  success: null,
}

export const initialState = (targeting = []) => {
  return {
    ..._initialState,
    value: [...targeting.filter((x) => !!x.tc)],
  }
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.overwriteTargetingValue:
      return { ...state, value: action.payload }
    case actions.setTcTargeting: {
      const { tc, tc_run_id, fb, google } = action.payload
      const existing = state.value.find(
        (targeting) => targeting.tc === tc && targeting.tc_run_id === tc_run_id,
      ) || { tc, tc_run_id }
      const updated = { ...existing, tc, tc_run_id }
      if (fb !== undefined) updated.fb = fb
      if (google !== undefined) updated.google = google
      return {
        ...state,
        value: [...state.value.filter((targeting) => targeting.tc !== tc), updated],
      }
    }
    case actions.setSuccess:
      return { ...state, success: true, error: null, loading: false }
    case actions.setError:
      return { ...state, error: action.payload, success: null, loading: false }
    case actions.setLoading:
      return { ...state, loading: true }
    case actions.reset:
      return { ..._initialState }
    default:
      return state
  }
}

export const getLocationTargetingModeOptions = (
  hasPrismaAccess,
  hasCustomAudience,
  hasTargetingLimitations = false,
) => {
  let options = []
  if (hasTargetingLimitations) {
    options = [
      {
        value: TARGETING_MODES.rangeLocations,
        text: 'Recommendations.geoTargeting.options.range_locations',
        order: 1,
      },
    ]
    return sortBy(options, 'order')
  } else if (hasPrismaAccess) {
    options = [
      {
        value: TARGETING_MODES.postalCodes,
        text: 'Recommendations.geoTargeting.options.postal_codes',
        order: 0,
      },
      {
        value: TARGETING_MODES.rangeLocations,
        text: 'Recommendations.geoTargeting.options.range_locations',
        order: 1,
      },
    ]
  } else {
    options = [
      {
        value: TARGETING_MODES.fdTargeting,
        text: 'Recommendations.geoTargeting.options.fd_targeting',
        order: 0,
      },
      {
        value: TARGETING_MODES.postalCodes,
        text: 'Recommendations.geoTargeting.options.postal_codes',
        order: 1,
      },
      {
        value: TARGETING_MODES.rangeLocations,
        text: 'Recommendations.geoTargeting.options.range_locations',
        order: 2,
      },
    ]
  }

  if (hasCustomAudience) {
    options = options.filter((x) => x.value !== TARGETING_MODES.fdTargeting)
  }

  return sortBy(options, 'order')
}

export const getDefaultTargetingMode = (
  hasPrismaAccess,
  hasCustomAudience,
  hasTargetingLimitations = false,
) => {
  if (hasTargetingLimitations) {
    return TARGETING_MODES.rangeLocations
  }
  return hasPrismaAccess || hasCustomAudience
    ? TARGETING_MODES.postalCodes
    : TARGETING_MODES.fdTargeting
}

// Deeply empty: undefined/null, '', [], {}, or any nesting thereof.
function isValueEmpty(value) {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value)) return value.every(isValueEmpty)
  if (typeof value === 'object') return Object.values(value).every(isValueEmpty)
  return false
}

/**
 * True when a platform's sub-object has nothing to send: every field empty,
 * audience/age included, except possibly a lone `targeting_mode` (which
 * FB_TC_TARGETING_SCHEMA rejects). Decides whether to drop the whole fb/google
 * key from an outgoing payload. Not geo-scoped — use isGeoTargetingEmpty for
 * anything about campaign_targeting.
 */
export function isPlatformTargetingEmpty(platformTargeting) {
  if (!platformTargeting) return true
  return Object.entries(platformTargeting).every(
    ([key, value]) => key === 'targeting_mode' || isValueEmpty(value),
  )
}

// True when a platform's sub-object has no postal_codes/range_locations
// content.
export function isGeoTargetingEmpty(platformTargeting) {
  return isPlatformTargetingEmpty(pickGeoTargetingFields(platformTargeting))
}

// Whether an active platform has no meaningful location data on this TC's own
// entry. A gated TC reads its real geo via withCampaignWideTargetingFallback.
export function isLocationTargetingEmpty(tcTargeting, platform) {
  if (!tcTargeting) return false

  const platformTargeting = tcTargeting[platform]
  const targetingMode = platformTargeting?.targeting_mode
  if (!targetingMode || targetingMode === TARGETING_MODES.fdTargeting) return false

  if (
    targetingMode === TARGETING_MODES.postalCodes &&
    isObjectEmpty(platformTargeting?.postal_codes?.countries)
  )
    return true

  if (
    targetingMode === TARGETING_MODES.rangeLocations &&
    !(
      platformTargeting?.range_locations?.included?.length ||
      platformTargeting?.range_locations?.excluded?.length
    )
  )
    return true

  return false
}

/**
 * Validates a TC's location targeting across every active platform; invalid if
 * any of them fails, since the shared Location modal writes them together.
 *
 * @param {object} [setup] required to validate a gated TC — its own entry is
 *   geo-stripped on save, so without this the check vacuously passes. The
 *   fallback is called with isFirstTc=true for every TC on purpose: validation
 *   wants "trust my own geo if present, else campaign_targeting" throughout.
 * @returns {{valid: boolean, code?: string}}
 */
export function validateLocationTargeting(
  targeting,
  tc,
  tc_run_id,
  hasCustomAudience,
  hasSavedAudience = false,
  platforms = [TARGETING_PLATFORMS.fb],
  setup = undefined,
) {
  const hasPrismaAccess = hasPartialServiceAccessTier(store.getState().user?.accessLevel)
  const rawTcTargeting = targeting?.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
  const tcTargeting = setup
    ? withCampaignWideTargetingFallback(rawTcTargeting, setup, true)
    : rawTcTargeting

  // If Taste Cluster uses a saved audience, we don't need geo targeting validation
  if (hasSavedAudience && !platforms.includes(TARGETING_PLATFORMS.google)) {
    return { valid: true }
  }

  if (!tcTargeting && hasPrismaAccess) {
    return { valid: false, code: 'NO_TC_TARGETING' }
  }

  for (const platform of platforms) {
    const platformTargeting = tcTargeting?.[platform]
    const targetingMode = platformTargeting?.targeting_mode

    if (!targetingMode) {
      // If no targeting mode is set for this platform yet, ignore validation for it
      continue
    }

    if (targetingMode === TARGETING_MODES.fdTargeting) continue

    if (targetingMode === TARGETING_MODES.postalCodes) {
      const countries = platformTargeting?.postal_codes?.countries
      if (!countries || !Object.values(countries).some((x) => x.length > 0)) {
        return { valid: false, code: 'NO_POSTAL_CODES' }
      }
      continue
    }

    if (targetingMode === TARGETING_MODES.rangeLocations) {
      const rangeLocations = platformTargeting?.range_locations
      if (!(rangeLocations?.included?.length > 0 || rangeLocations?.excluded?.length > 0)) {
        return { valid: false, code: 'NO_RANGE_LOCATIONS' }
      }
      continue
    }

    return { valid: false, code: 'UNKNOWN' }
  }

  return { valid: true }
}
